import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useWixSdk } from '@wix/yoshi-flow-editor';
import { getVodSettings } from '../../api/private/get-vod-settings';
import { isUnsavedTemplateSite } from '../../redux/hydrated-data/hydrated-data';

// `appEngaged` / `PremiumIntent` are editor-only TPA-SDK members that exist at
// runtime but are missing from the typed `IWixSettings` surface, so we augment it.
enum PremiumIntent {
  FREE = 'FREE',
  PAID = 'PAID',
}

interface PremiumIntentSettings {
  appEngaged: (premiumIntent: PremiumIntent) => void;
  PremiumIntent: typeof PremiumIntent;
}

// `getVodSettings` resolves to `{}` for new sites (404 fallback), so the only
// field we read is treated as optional.
interface VodSettings {
  biUserUploadedFiles?: boolean;
}

const PremiumIntentUpdater: FC = () => {
  const { Wix } = useWixSdk();
  const isUnsavedTemplate = useSelector(isUnsavedTemplateSite);

  useEffect(() => {
    // `useWixSdk()` returns `{ Wix: null }` in the OOI-in-editor environment, so
    // this whole effect is a no-op there (see the note on `appEngaged` below).
    if (isUnsavedTemplate || !Wix) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      const vodSettings: VodSettings = await getVodSettings();

      if (cancelled) {
        return;
      }

      const settings = Wix.Settings as typeof Wix.Settings &
        PremiumIntentSettings;

      // `appEngaged` reports premium intent to the editor host, which turns it
      // into the `APP_ENGAGEMENT` BI event (used for premium-upgrade targeting).
      // It is
      // wired in the editor ONLY as a TPA-SDK postMessage handler — there is no
      // editor-SDK / OOI / platform-API equivalent (verified against santa-editor
      // + editor-platform). So in OOI-in-editor, where the TPA `Wix` SDK is
      // absent, the signal simply isn't emitted. If/when OOI needs it, this must
      // be re-sourced (e.g. an app-side BI event) — confirm the consumer first.
      settings.appEngaged(
        vodSettings.biUserUploadedFiles
          ? settings.PremiumIntent.PAID
          : settings.PremiumIntent.FREE,
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [Wix, isUnsavedTemplate]);

  return null;
};

export default PremiumIntentUpdater;
